// file: `react/src/pages/Signup/Signup.tsx`:

import React, { useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { expressURL } from "../../utils/networkUtils";
import { firstGrapheme } from '../../utils/singleEmoji.ts';
import { newPasswordKdf, derivePasswordKeys } from '../../crypto/passwordSplit.ts';
import { wrapPrivateKeyWithWrapKey } from '../../crypto/identity.ts';
import { safeJoinNextPath, joinTokenFromNextPath } from '../../utils/joinPath.ts';

const CONFIRMATION_HINTS = [
  'check your email',
  'awaiting confirmation',
  'could not send the confirmation email',
  'resend confirmation',
];

function needsConfirmationFlow(message: string) {
  const lower = message.toLowerCase();
  return CONFIRMATION_HINTS.some((hint) => lower.includes(hint));
}

function Signup() {
  const [searchParams] = useSearchParams();
  const nextPath = safeJoinNextPath(searchParams.get('next'));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emoji, setEmoji] = useState('');
  const [message, setMessage] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleResend = async (targetEmail: string) => {
    const trimmed = targetEmail.trim();
    if (!trimmed) {
      setResendMessage('Enter your email address first.');
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      const response = await axios.post(`${expressURL()}/auth/resend-confirmation`, {
        email: trimmed,
      });
      setResendMessage(typeof response.data === 'string' ? response.data : 'If that email needs confirmation, we sent a confirmation link.');
    } catch (error) {
      const detail = error.response?.data;
      const text = typeof detail === 'string' ? detail : (detail?.error || error.message || 'unknown error');
      setResendMessage('Error: ' + text);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    setMessage('');
    setResendMessage('');
    setSubmitting(true);
    try {
      const kdf = newPasswordKdf();
      const keys = await derivePasswordKeys(password, kdf);
      const wrapped = await wrapPrivateKeyWithWrapKey(keys.wrapKey);
      const response = await axios.post(`${expressURL()}/auth/signup`, {
        name,
        email: trimmedEmail,
        emoji,
        password: keys.authKey,
        passwordKdf: kdf,
        joinToken: joinTokenFromNextPath(nextPath),
        crypto: {
          publicKeyJwk: wrapped.identity.publicKeyJwk,
          wrappedPrivateKey: wrapped.wrappedPrivateKey
        }
      });
      const text = typeof response.data === 'string' ? response.data : 'Signup successful.';
      setMessage(text);
      setShowResend(needsConfirmationFlow(text));
      setResendEmail(trimmedEmail);
    } catch (error) {
      const errorMessage = error.response?.data || 'An error occurred. Please try again.';
      const text = 'Error: ' + errorMessage;
      setMessage(text);
      setShowResend(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='main-content'>
      <Container className='mb-5'>
        <h1 className="display-4 mb-3 mt-3">Signup</h1>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Name</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="text"
                autoComplete="name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Email address</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="email"
                autoComplete="username"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Emoji</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="text"
                name="emoji"
                value={emoji}
                onChange={(e) => setEmoji(firstGrapheme(e.target.value))}
                placeholder="One emoji"
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Password</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="password"
                autoComplete="new-password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Col>
          </Row>
          <button className='btn btn-primary' type="submit" disabled={submitting}>
            {submitting ? 'Signing up…' : 'Signup'}
          </button>
          {message && <p className="mt-3">{message}</p>}
          {showResend && (
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={resending}
                onClick={() => handleResend(resendEmail || email)}
              >
                {resending ? 'Sending…' : 'Resend confirmation email'}
              </button>
              {resendMessage && <p className="mt-2 mb-0">{resendMessage}</p>}
            </div>
          )}
        </form>

        {!showResend && (
        <div className="mt-4 pt-3 border-top">
          <h2 className="h5">Need to confirm your account?</h2>
          <p className="text-muted mb-2">Enter your email and we will send a new confirmation link.</p>
          <Row className="align-items-end">
            <Col sm={8} md={6} lg={4} className="mb-2">
              <Form.Control
                type="email"
                autoComplete="username"
                name="resend-email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Email address"
              />
            </Col>
            <Col sm="auto" className="mb-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={resending}
                onClick={() => handleResend(resendEmail)}
              >
                {resending ? 'Sending…' : 'Resend confirmation'}
              </button>
            </Col>
          </Row>
          {resendMessage && <p className="mt-2">{resendMessage}</p>}
        </div>
        )}

        <p className="mt-3">
          Already have an account?{' '}
          <Link to={nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : '/login'}>Login</Link>
        </p>
        {nextPath && !message && (
          <p className="text-muted">Check your email after signup. That link signs you in and opens the chat.</p>
        )}
      </Container>
    </div>

  );
}

export default Signup;
